/**
 * ReleaseModeChoice — the Zwolnij confirmation's whole-versus-selected-period
 * control (CU-86cbh03pj, partial-reservation-release task 4.1).
 *
 * Shown only when the caller's selection under-covers at least one own hold
 * (`unified-select-popover.tsx`'s `hasUnderCoveredHold`), per the
 * `unified-availability-interaction` spec's "Unified SELECT popover" requirement:
 * "Zwolnij SHALL let the caller choose, before confirming, between releasing that
 * reservation's ENTIRE period or releasing ONLY the selected period". Reuses the
 * sanctioned `RadioGroup`/`RadioGroupItem` two-option pattern
 * `UnifiedSelectOptionDialog` already established (`packages/ui/AGENTS.md`:
 * preserve the compound hierarchy, never flatten it).
 */
import { RadioGroup, RadioGroupItem } from '@ooh/ui/components/radio-group';
import type { BatchReleaseMode } from '@ooh/contracts';

export interface ReleaseModeChoiceLabels {
  title: string;
  wholeLabel: string;
  selectedLabel: string;
}

export interface ReleaseModeChoiceProps {
  /** `null` before the caller has picked either option. */
  value: BatchReleaseMode | null;
  onChange: (mode: BatchReleaseMode) => void;
  /** True while a preview/commit request for the current mode is in flight — blocks
   * flipping to another mode mid-request, which would otherwise let an out-of-order
   * response land for a mode the caller already moved away from. */
  disabled: boolean;
  labels: ReleaseModeChoiceLabels;
}

export function ReleaseModeChoice({ value, onChange, disabled, labels }: ReleaseModeChoiceProps) {
  return (
    <div className="space-y-1.5" data-testid="unified-select-release-mode-choice">
      <p className="text-sm font-medium">{labels.title}</p>
      <RadioGroup
        // Always a defined string (never `undefined`) so Radix's RadioGroup stays
        // controlled from first render — passing `undefined` until a choice is
        // made, then a real value once one is, flips it from uncontrolled to
        // controlled mid-lifetime, which React warns about.
        value={value ?? ''}
        onValueChange={(next) => onChange(next as BatchReleaseMode)}
      >
        <RadioGroupItem
          value="full"
          label={labels.wholeLabel}
          disabled={disabled}
          data-testid="unified-select-release-mode-full"
        />
        <RadioGroupItem
          value="selected"
          label={labels.selectedLabel}
          disabled={disabled}
          data-testid="unified-select-release-mode-selected"
        />
      </RadioGroup>
    </div>
  );
}
